import { CSpinner } from "@coreui/react";
import {
  DriverProfileStatus,
  DriverProfileStatusDisplayValue,
} from "../../helpers/enums";

const STATUS_TONES: Record<string, { bg: string; color: string }> = {
  success: { bg: "#eaf8ef", color: "#239b4a" },
  danger: { bg: "#fdecec", color: "#d92d20" },
  primary: { bg: "#fff1e7", color: "#ff7722" },
};

function getTone(status?: string) {
  if (status === DriverProfileStatus.APPROVED) return STATUS_TONES.success;
  if (
    status === DriverProfileStatus.DECLINED ||
    status === DriverProfileStatus.BLOCKED
  )
    return STATUS_TONES.danger;
  return STATUS_TONES.primary;
}

export function StatusTag({
  status,
  label,
}: {
  status?: string;
  label: string;
}) {
  const tone = getTone(status);
  return (
    <span
      className="badge rounded-pill px-3 py-2 text-uppercase"
      style={{
        background: tone.bg,
        color: tone.color,
        fontSize: 11,
        letterSpacing: ".04em",
      }}
    >
      {label}
    </span>
  );
}

interface Props {
  fullName?: string;
  profilePicture?: string | null;
  status?: string;
  statusLabel: DriverProfileStatusDisplayValue | string;
  isOnline: boolean;
  onlineLoading: boolean;
  onToggleOnline: (checked: boolean) => void;
  uploadHref: string;
  showApprove: boolean;
  showDecline: boolean;
  showPending: boolean;
  approveLoading: boolean;
  declineLoading: boolean;
  pendingLoading: boolean;
  actionsDisabled: boolean;
  onApprove: () => void;
  onDecline: () => void;
  onPending: () => void;
  LinkComponent: React.ElementType;
}

export default function DriverDetailHeader({
  fullName,
  profilePicture,
  status,
  statusLabel,
  isOnline,
  onlineLoading,
  onToggleOnline,
  uploadHref,
  showApprove,
  showDecline,
  showPending,
  approveLoading,
  declineLoading,
  pendingLoading,
  actionsDisabled,
  onApprove,
  onDecline,
  onPending,
  LinkComponent,
}: Props) {
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 bg-white rounded-4 shadow-sm p-3 my-3 w-100">
      <div className="d-flex align-items-center gap-3 min-w-0">
        <img
          src={
            profilePicture ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName ?? "")}`
          }
          alt=""
          width={56}
          height={56}
          className="rounded-circle border border-2 border-danger object-fit-cover bg-white flex-shrink-0"
        />
        <div className="min-w-0">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <h5 className="fs-4 fw-bold text-capitalize mb-0">{fullName}</h5>
            <StatusTag status={status} label={String(statusLabel)} />
          </div>

          <div className="d-flex align-items-center gap-2 mt-2">
            <label className="switch mb-0">
              <input
                className="form-check-status"
                type="checkbox"
                checked={isOnline}
                onChange={(e) => onToggleOnline(e.target.checked)}
                disabled={onlineLoading}
                id="isOnline"
              />
              <span className="slider round form-check-status"></span>
            </label>
            <label
              htmlFor="isOnline"
              className={`font-medium mb-0 text-sm ${
                isOnline ? "text-success" : "text-red"
              }`}
            >
              {isOnline ? "En ligne" : "Hors ligne"}
            </label>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2">
        <LinkComponent
          className="btn btn-outline-success rounded-pill py-2 px-4"
          to={uploadHref}
        >
          Upload documents
        </LinkComponent>

        {showApprove && (
          <button
            className="btn btn-success shadow-secondary rounded-pill py-2 px-4 text-white"
            onClick={onApprove}
            disabled={approveLoading || actionsDisabled}
          >
            {approveLoading ? <CSpinner size="sm" /> : "Accepter"}
          </button>
        )}
        {showPending && (
          <button
            className="btn btn-outline-primary rounded-pill py-2 px-4"
            onClick={onPending}
            disabled={pendingLoading || actionsDisabled}
          >
            {pendingLoading ? <CSpinner size="sm" /> : "En attente"}
          </button>
        )}
        {showDecline && (
          <button
            className="btn btn-danger shadow-primary rounded-pill py-2 px-4 text-white"
            onClick={onDecline}
            disabled={declineLoading || actionsDisabled}
          >
            {declineLoading ? <CSpinner size="sm" /> : "Refuser"}
          </button>
        )}
      </div>
    </div>
  );
}
